import { useEffect, useState } from "react";
import DragAndDropContainer from "../../../../ui library/components/file-upload/DragAndDropContainer";
import AutoCompleteDropdown from "../../../../ui library/components/dropdowns/AutoCompleteDropdown";
import BasicDropdown from "../../../../ui library/components/dropdowns/BasicDropdown";
import BasicButton from "../../../../ui library/components/buttons/BasicButton";
import useService from "../../../../../hooks/useService";
import { useParams } from "react-router-dom";
import DocumentUploadHeader from "./DocumentUploadHeader";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../global/ResponseModal";
import LoadingIndicator from "../../../../global/LoadingIndicator";
import { useDocumentListStore } from "../../ViewDocuments/hooks/useDocumentListStore";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import BillAccordionSummary from "./CustomAccordion/BillAccordionSummary";
import BillAccordionDetails from "./CustomAccordion/BillAccordionDetails";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Accordion from "../../../../ui library/components/accordions/Accordion";
import AccordionList from "../../../../ui library/components/accordions/AccordionList";
import { useUploadDocumentStore } from "../hooks/useUploadDocumentStore";

let globalIdCounter = 0;
const generateId = () => ++globalIdCounter;

const DocumentUploadWithApptBills = ({
  selectedProviderId,
  isLaunchedFromAppointmentsTab,
  setIsLaunchedFromAppointmentsTab,
}) => {
  const fieldOptionRepository = useService(
    "repositories/field-option-repository"
  );
  const documentRepository = useService("repositories/document-repository");
  const documentValidateRepository = useService(
    "repositories/document-validate-repository"
  );
  const providerClientStatusRepository = useService(
    "repositories/provider-client-status-repository"
  );

  const { setServiceLimit, setServiceOffset, setProvider } =
    useUploadDocumentStore();
  const { id } = useParams();

  const [fileReq, setFileReq] = useState("");
  const [documentsOptions, setDocumentsOptions] = useState(null);
  const [documentValidations, setDocValidations] = useState(null);
  const [providers, setProviders] = useState(null);
  const [isDocSourceRequired, setIsDocSourceRequired] = useState(false);
  const [isRelatedProviderRequired, setIsRelatedProviderRequired] =
    useState(false);
  const [isReferralFromRequired, setIsReferralFromRequired] = useState(false);
  const [isReferralToRequired, setIsReferralToRequired] = useState(false);
  const [isApptBillCreationAllowed, setIsApptBillCreationAllowed] =
    useState(true);
  const [maxDocSize, setMaxDocSize] = useState("Max Size: 100MB");
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedDocSource, setSelectedDocSource] = useState(null);
  const [selectedRelatedProvider, setSelectedRelatedProvider] = useState(null);
  const [selectedReferralFrom, setSelectedReferralFrom] = useState(null);
  const [selectedReferralTo, setSelectedReferralTo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [errorStatus, setErrorStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initDocType, setInitDocType] = useState(null);
  const [initDocSource, setInitDocSource] = useState("");
  const [initRelatedProvider, setInitRelatedProvider] = useState("");
  const setIsUploadModalOpen = useDocumentListStore(
    (state) => state.setIsUploadModalOpen
  );
  const [accordionList, setAccordionList] = useState([]);
  const defaultSourceIdFromAppointmentsTab = 3;

  useEffect(() => {
    setServiceOffset(0);
    setServiceLimit(25);
    setAccordionList(new Map());
  }, [selectedRelatedProvider]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [docTypesRaw, sourcesRaw, validations, providerClients] =
          await Promise.all([
            fieldOptionRepository.query({
              content_type: "document",
              field_name: "document_type",
              limit: 100,
            }),
            fieldOptionRepository.query({
              content_type: "document",
              field_name: "source",
            }),
            documentValidateRepository.get(),
            providerClientStatusRepository.query({ case: id }),
          ]);

        const providers = providerClients.map((client) => client.provider);

        const docTypeOptions =
          docTypesRaw?.map((opt) => ({
            id: +opt.key,
            label: opt.label,
            additionalAttributes: opt.additionalAttributes,
          })) || [];

        const sourceOptions =
          sourcesRaw?.map((opt) => ({
            id: +opt.key,
            label: opt.label,
          })) || [];

        const mappedProviders = providers.map((p) => ({
          id: p.id,
          label: p.name,
          providerTypes: p.providerTypes,
        }));

        setDocumentsOptions({ docTypeOptions, sourceOptions });
        setDocValidations(validations);
        setMaxDocSize(validations?.maxFileSize);
        setProviders(mappedProviders);

        if (isLaunchedFromAppointmentsTab) {
          const selectedDocType = docTypeOptions.find((d) => d.additionalAttributes.isDefault === true);
          const selectedDocSource = sourceOptions.find((s) => s.id === defaultSourceIdFromAppointmentsTab);
          const selectedRelatedProvider = mappedProviders.find(
            (p) => p.id === selectedProviderId
          );

          setInitDocType(selectedDocType);
          setInitDocSource(selectedDocSource);
          setInitRelatedProvider(selectedRelatedProvider);

          setSelectedDocType(selectedDocType);
          setSelectedDocSource(selectedDocSource);
          setSelectedRelatedProvider(selectedRelatedProvider);

          docTypeValidate(validations, selectedDocType);
        }
      } catch (error) {
        handleNeedsTypeFail(error);
      }
    };

    fetchOptions();
  }, [
    fieldOptionRepository,
    documentValidateRepository,
    providerClientStatusRepository,
    id,
  ]);

  const onClearDocType = () => {
    resetAll();
  };

  const resetAll = () => {
    setFileReq("");
    setIsRelatedProviderRequired(false);
    setIsDocSourceRequired(false);
    setIsReferralFromRequired(false);
    setIsReferralToRequired(false);
    setSelectedDocType(initDocType);
    setSelectedDocSource(initDocSource);
    setSelectedRelatedProvider(initRelatedProvider);
    setSelectedReferralFrom("");
    setSelectedReferralTo("");
    setSelectedFile("");
    setAccordionList(new Map());

    if (isLaunchedFromAppointmentsTab) {
      docTypeValidate(documentValidations, initDocType);
    }
  };

  const docTypeValidate = (docValidationList, value) => {
    setFileReq(docValidationList?.validExtensions[value?.id].join(", "));
    setIsDocSourceRequired(
      docValidationList?.required?.source?.includes(value?.id)
    );
    if (!isLaunchedFromAppointmentsTab) {
      setIsRelatedProviderRequired(
        docValidationList?.required?.provider?.includes(value?.id)
      );
    } else {
      setIsRelatedProviderRequired(true);
    }
    setIsReferralFromRequired(
      docValidationList?.required?.referral_from?.includes(value?.id)
    );
    setIsReferralToRequired(
      docValidationList?.required?.referral_to?.includes(value?.id)
    );
    if (
      value?.additionalAttributes?.canAddAppointments &&
      value?.additionalAttributes?.canAddBills
    ) {
      setIsApptBillCreationAllowed(true);
    } else {
      setIsApptBillCreationAllowed(false);
    }
  };

  const onChangeDocType = (e, value) => {
    setSelectedDocType(value);
    if (!value) {
      onClearDocType();
      return;
    }
    docTypeValidate(documentValidations, value);
  };

  const onChangeDocSource = (e, value) => {
    setSelectedDocSource(value);
  };

  const onChangeRelatedProvider = (e, value) => {
    setSelectedRelatedProvider(value);
    setProvider(value);
  };

  const onChangeReferralFrom = (e, value) => {
    setSelectedReferralFrom(value);
  };

  const onChangeReferralTo = (e, value) => {
    setSelectedReferralTo(value);
  };

  const onChangeFile = (e, value) => {
    setSelectedFile(value);
  };

  const onDeleteAppointmentRow = (idToDelete) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      newMap.delete(idToDelete);
      return newMap;
    });
  };

  const onDeleteBillRow = (apptId, billIdToDelete) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      newMap.get(apptId).bills.delete(billIdToDelete);
      return newMap;
    });
  };

  const updateAccordionField = (id, fieldName, newValue) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      const entry = newMap.get(id);
      if (entry) {
        newMap.set(id, { ...entry, [fieldName]: newValue });
      }
      return newMap;
    });
  };

  const handleUpdateProviderTypeId = (id, newProviderType) => {
    updateAccordionField(id, "provider_type_id", newProviderType?.id || null);
  };

  const handleUpdateAppointmentDate = (id, newDate) => {
    if (!newDate) {
      newDate = new Date(new Date().setHours(12, 0, 0, 0));
    }
    updateAccordionField(id, "scheduled", newDate);
  };

  const handleUpdateBillValues = (apptId, billId, updatedFields) => {
    setAccordionList((prev) => {
      const newMap = new Map(prev);
      const appointment = newMap.get(apptId);
      if (!appointment) return prev;

      const bill = appointment.bills.get(billId);
      if (!bill) return prev;

      appointment.bills.set(billId, { ...bill, ...updatedFields });
      return newMap;
    });
  };

  const handleUpdateBillTotal = (apptId, billId, newTotal) => {
    handleUpdateBillValues(apptId, billId, { price: newTotal });
  };

  const handleUpdateServiceId = (
    apptId,
    billId,
    newServiceId,
    newServiceInfo
  ) => {
    handleUpdateBillValues(apptId, billId, {
      service_id: newServiceId,
      service_info: newServiceInfo,
    });
  };

  const onCreateAppBtnClick = () => {
    setAccordionList((prev) => {
      const id = generateId();
      const newEntry = {
        id,
        scheduled: null,
        provider_type_id: null,
        bills: new Map(),
        deleteRow: () => onDeleteAppointmentRow(id),
        updateProviderTypeId: handleUpdateProviderTypeId,
        updateScheduled: handleUpdateAppointmentDate,
        onAddBillBtnClick: onAddBillBtnClick,
      };
      const newMap = new Map(prev);
      newMap.set(id, newEntry);
      return newMap;
    });
  };

  const onAddBillBtnClick = (apptId) => {
    setAccordionList((prev) => {
      const id = generateId();
      const currentAppointment = prev.get(apptId);
      const newEntry = {
        id,
        service_id: "",
        service_info: null,
        price: null,
        deleteRow: () => onDeleteBillRow(apptId, id),
        updateServiceId: handleUpdateServiceId,
        updatePrice: handleUpdateBillTotal,
      };
      const newMap = new Map(prev);
      currentAppointment.bills.set(id, newEntry);
      newMap.set(apptId, currentAppointment);
      return newMap;
    });
  };

  const onUploadBtnClick = async () => {
    try {
      setLoading(true);

      const postData = {
        case: id,
        document_type: selectedDocType?.id,
        file: selectedFile,
        ...(isDocSourceRequired || selectedDocSource?.id
          ? { source: selectedDocSource?.id }
          : {}),
        ...(isRelatedProviderRequired && selectedRelatedProvider?.id
          ? { provider: selectedRelatedProvider.id }
          : {}),
        ...(isLaunchedFromAppointmentsTab ||
        (isReferralFromRequired && selectedReferralFrom?.id)
          ? { referral_from: selectedReferralFrom?.id }
          : {}),
        ...(isReferralToRequired && selectedReferralTo?.id
          ? { referral_to: selectedReferralTo.id }
          : {}),
      };

      if (accordionList && accordionList.size > 0) {
        postData.appointments = Array.from(accordionList.values()).map(
          (appointment) => ({
            scheduled:
              appointment.scheduled?.toISOString() ||
              new Date(new Date().setHours(12, 0, 0, 0)).toISOString(),
            provider_type_id: appointment?.provider_type_id,
            ...(appointment?.bills?.size > 0 && {
              bills: Array.from(appointment.bills.values()).map(
                ({ service_id, price }) => ({
                  service_id,
                  price,
                })
              ),
            }),
          })
        );
      }

      const res = await documentRepository.upload(postData);
      setUploadSuccess(true);
      return res;
    } catch (error) {
      handleNeedsTypeFail(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setUploadSuccess(false);
    resetAll();
    setIsUploadModalOpen(false);
  };
  const handleNeedsTypeFail = (error) => {
    setUploadFailed(true);
    let errArr = ApiRequestErrorHandler(error?.response?.data || error);
    setErrorArray(errArr);
  };

  return (
    <div className="h-full">
      <DocumentUploadHeader maxDocSize={maxDocSize} fileReq={fileReq} />

      <div className="py-2">
        <div className="w-full my-8 mx-auto" data-testid="container-docTypes">
          <AutoCompleteDropdown
            initValue={selectedDocType}
            label="Document Types"
            data={documentsOptions?.docTypeOptions || []}
            onValueChange={(e, value) => {
              onChangeDocType(e, value);
            }}
          />
        </div>

        <div className="w-full my-8 mx-auto" data-testid="container-docSource">
          <BasicDropdown
            initValue={selectedDocSource}
            label={"Document Source" + (isDocSourceRequired ? "*" : "")}
            data={documentsOptions?.sourceOptions || []}
            onValueChange={onChangeDocSource}
            addNoSelectionOption={!isDocSourceRequired}
          />
        </div>

        {isRelatedProviderRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-relatedProvider"
          >
            <BasicDropdown
              initValue={selectedRelatedProvider}
              label={"Related Provider*"}
              data={providers || []}
              onValueChange={onChangeRelatedProvider}
              addNoSelectionOption={false}
              disabled={isLaunchedFromAppointmentsTab}
            />
          </div>
        )}

        {(isReferralFromRequired || isReferralToRequired) && (
          <div className="mx-4 mt-1 mb-10 p-4 text-black rounded-sm bg-yellow-100 text-sm">
            <strong>NOTE: </strong>Only providers that are already assigned to
            the client's case can be chosen below. If the provider needed is not
            shown below, please add them to the case before uploading the
            referral file.
          </div>
        )}

        {isReferralFromRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-referralFrom"
          >
            <BasicDropdown
              initValue={selectedReferralFrom}
              label={"Referral From*"}
              data={providers || []}
              onValueChange={(e, value) => {
                onChangeReferralFrom(e, value);
              }}
              addNoSelectionOption={false}
            />
          </div>
        )}

        {isReferralToRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-referralTo"
          >
            <BasicDropdown
              initValue={selectedReferralTo}
              label={"Referral To*"}
              data={providers || []}
              onValueChange={(e, value) => {
                onChangeReferralTo(e, value);
              }}
              addNoSelectionOption={false}
            />
          </div>
        )}

        {isApptBillCreationAllowed &&
          selectedRelatedProvider?.id !== undefined && (
            <div className="flex flex-col" key={accordionList}>
              {accordionList.size > 0 && (
                <fieldset
                  component="fieldset"
                  className="group px-4 py-2 border-[1px] mb-4 rounded-md border-gray-300 hover:border-gray-700"
                >
                  <legend className="text-xs text-gray-500 p-1">
                    Appointments
                  </legend>
                  <AccordionList
                    accordionListData={accordionList}
                    accordionVariant="default"
                    summaryContent={BillAccordionSummary}
                    expandedContent={<BillAccordionDetails />}
                    expandIcon={<ExpandMore className="text-gray-500" />}
                    shrinkIcon={<ExpandLess className="text-gray-500" />}
                    expandOnIconClickOnly={true}
                  >
                    <Accordion />
                  </AccordionList>
                </fieldset>
              )}
              <div className="w-100 flex flex-row flex-row-reverse">
                <BasicButton
                  text="Create Appointment"
                  className={`w-full bg-green-600 px-4 py-2 items-center self-center text-lg hover:bg-green-700`}
                  icon={<AddCircleOutlineRoundedIcon />}
                  onClick={onCreateAppBtnClick}
                />
              </div>
            </div>
          )}

        <div
          className="w-full my-8 mx-auto"
          data-testid="container-dragAndDropBox"
        >
          <DragAndDropContainer
            initValue={selectedFile}
            onValueChange={onChangeFile}
          />
        </div>

        <div
          className="w-full my-8 mx-auto text-center flex flex-row justify-evenly gap-4"
          data-testid="container-uploadButton"
        >
          <BasicButton
            text="Upload Document"
            className="w-1/2 bg-blue-600 px-4 py-2 items-center self-center text-lg hover:bg-blue-700"
            onClick={onUploadBtnClick}
          />
          <BasicButton
            variant="outlined"
            text="Clear"
            className="w-1/2 px-4 py-2 text-blue-600 items-center self-center text-lg hover:bg-blue-100"
            onClick={resetAll}
          />
        </div>
      </div>
      <LoadingIndicator isLoading={loading} />
      <ResponseModal
        title="Update Success"
        description="The image/document was successfully updated"
        openBool={uploadSuccess}
        setOpenBool={setUploadSuccess}
        handleCloseFunc={handleUploadSuccess}
      />
      <ResponseModal
        title="Update failed"
        isError={true}
        openBool={uploadFailed}
        setOpenBool={setUploadFailed}
        errorMessage={errorArray}
      />
    </div>
  );
};

export default DocumentUploadWithApptBills;
